import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShiftsService } from '../services/supabase/shiftsService';
import { CareReportSubmissionService } from '../services/supabase/careReportSubmissionService';
import { mapShiftRow } from '../utils/shiftUtils';
import { determinePriceByDate, minsInSpanEdited, hoursInSpan } from '../utils/hoursUtils';
import { QuickBooksService } from '../services/quickbooks/quickbooksService';
import './invoicingPage.css';

function formatDate(datetimeStr) {
  if (!datetimeStr) return '—';
  return new Date(datetimeStr).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatDateTime(datetimeStr) {
  if (!datetimeStr) return '—';
  return new Date(datetimeStr).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function getCaretakerName(shift, caretakerList) {
  const match = caretakerList?.find(c => c.email === shift.caretakerEmail);
  return match?.name ?? shift.caretaker ?? '—';
}

export default function InvoicingPage({ clientList, caretakerList, draft, onDraftChange, qbConnected }) {
  const { selectedClient, startDate, endDate, results } = draft;
  const setSelectedClient = (v) => onDraftChange(d => ({ ...d, selectedClient: v }));
  const setStartDate      = (v) => onDraftChange(d => ({ ...d, startDate: v }));
  const setEndDate        = (v) => onDraftChange(d => ({ ...d, endDate: v }));
  const setResults        = (v) => onDraftChange(d => ({ ...d, results: v }));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [copied, setCopied] = useState(false);
  const [qbLoading, setQbLoading] = useState(false);
  const [qbStatus, setQbStatus] = useState(''); // success or error message

  const activeClients = (clientList ?? [])
    .filter(c => !c.is_deactivated)
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleGenerate = async () => {
    if (!selectedClient) { setError('Please select a client.'); return; }
    if (!startDate)       { setError('Please select a start date.'); return; }
    if (!endDate)         { setError('Please select an end date.'); return; }
    if (endDate < startDate) { setError('End date must be after start date.'); return; }

    setError('');
    setLoading(true);
    setResults(null);

    try {
      const DAY = 24 * 60 * 60 * 1000;
      const startMs = new Date(startDate + 'T00:00:00').getTime();
      const endMs   = new Date(endDate   + 'T00:00:00').getTime() + DAY;

      const [rawShifts, careReports] = await Promise.all([
        // Pad DB query by 1 day each side so shifts with edited start times
        // that cross the boundary are still fetched, then filter client-side.
        ShiftsService.getShiftsForClientInRange(selectedClient, startMs - DAY, endMs + DAY),
        CareReportSubmissionService.getReportsForClient(selectedClient),
      ]);

      const shifts = rawShifts
        .map(mapShiftRow)
        .filter(s => {
          const effectiveStart = new Date(s.editedStartTime || s.start).getTime();
          return effectiveStart >= startMs && effectiveStart < endMs;
        });
      const client = clientList.find(c => c.name === selectedClient);

      // Build per-shift rows (all shifts)
      const shiftRows = shifts
        .sort((a, b) => new Date(a.start) - new Date(b.start))
        .map(s => {
          const shiftMs = new Date(s.start).getTime();
          const effectiveStart = new Date(s.editedStartTime || s.start);
          const effectiveEnd = new Date(s.editedEndTime || s.end);
          const workedMins = (effectiveEnd.getTime() - effectiveStart.getTime()) / 1000 / 60;
          const hoursWorked = parseFloat(hoursInSpan(workedMins));

          // Flat fee for client only when a client-specific amount is set.
          // A shift flat-fee'd for the caregiver but with no client amount bills hourly.
          const isClientFlatFee = s.isFlatFee && Number(s.flatFeeAmountClient) > 0;

          let rate, hours, lineTotal, flatFee;
          if (isClientFlatFee) {
            rate = null; hours = null; lineTotal = null;
            flatFee = Number(s.flatFeeAmountClient);
          } else {
            // minsInSpanEdited skips isFlatFee shifts, so fall back to epoch math for those
            const billingMins = s.isFlatFee
              ? workedMins
              : minsInSpanEdited([s], { excludeCleaning: false });
            rate = determinePriceByDate(client.rate, shiftMs);
            hours = parseFloat(hoursInSpan(billingMins));
            lineTotal = parseFloat((hours * rate).toFixed(2));
            flatFee = null;
          }

          const mileage = Number(s.shiftMileage) || 0;

          // Match care report by shift_id (UUID) — timestamp matching is unreliable
          const report = careReports.find(r => r.shift_id === s.docID);
          const reimbursement = report ? (Number(report.supplies_amount) || 0) : 0;

          return {
            id: s.docID,
            date: s.editedStartTime || s.start,
            end: s.editedEndTime || s.end,
            caretaker: getCaretakerName(s, caretakerList),
            isFlatFee: isClientFlatFee,
            hours,
            hoursWorked,
            rate,
            lineTotal,
            flatFee,
            mileage,
            reimbursement,
          };
        });

      // Subtotals
      const totalHoursAmt = shiftRows
        .filter(r => !r.isFlatFee)
        .reduce((sum, r) => sum + (r.lineTotal || 0), 0);

      const totalFlatFees = shiftRows
        .reduce((sum, r) => sum + (r.flatFee || 0), 0);

      const totalMileage = shiftRows
        .reduce((sum, r) => sum + r.mileage, 0) * 0.45;

      const totalReimbursements = shiftRows
        .reduce((sum, r) => sum + r.reimbursement, 0);

      const grandTotal = totalHoursAmt + totalFlatFees + totalMileage + totalReimbursements;

      setResults({ shiftRows, totalHoursAmt, totalFlatFees, totalMileage, totalReimbursements, grandTotal });
    } catch (err) {
      setError('Failed to generate invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => `$${Number(n).toFixed(2)}`;

  const handleSendToQB = async () => {
    if (!results) return;
    setQbLoading(true);
    setQbStatus('');
    try {
      const periodLabel = `${new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${new Date(endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      const result = await QuickBooksService.createInvoice({
        clientName: selectedClient,
        periodLabel,
        shiftRows: results.shiftRows.map(r => ({
          date: r.date,
          end: r.end,
          caretaker: r.caretaker,
          isFlatFee: r.isFlatFee,
          hours: r.hours,
          rate: r.rate,
          lineTotal: r.lineTotal,
          flatFee: r.flatFee,
        })),
        totals: {
          hoursAmt: results.totalHoursAmt,
          flatFees: results.totalFlatFees,
          mileage: results.totalMileage,
          mileageMiles: results.shiftRows.reduce((sum, r) => sum + r.mileage, 0),
          reimbursements: results.totalReimbursements,
          grandTotal: results.grandTotal,
        },
      });
      setQbStatus(`✓ Invoice #${result.invoiceNumber} created in QuickBooks ($${Number(result.totalAmt).toFixed(2)})`);
    } catch (err) {
      console.error('[QB Invoice]', err);
      setQbStatus(`Error: ${err.message}`);
    } finally {
      setQbLoading(false);
    }
  };

  return (
    <div className="invoicingPageDiv">
      <div className="invoicingPageHeader">
        <Link to="/" className="backToHomeLink">← Back to Home</Link>
        <h2 className="invoicingPageTitle">Invoicing</h2>
      </div>
      <div className="formSep"></div>

      {/* Create Invoice form */}
      <div className="invoiceFormSection">
        <h3 className="invoiceFormTitle">Create Invoice</h3>

        <div className="invoiceFormGrid">
          <label className="invoiceLabel">Client</label>
          <select
            className="invoiceSelect"
            value={selectedClient}
            onChange={e => setSelectedClient(e.target.value)}
          >
            <option value="">— select client —</option>
            {activeClients.map(c => (
              <option key={c.docID} value={c.name}>{c.name}</option>
            ))}
          </select>

          <label className="invoiceLabel">Start Date</label>
          <input
            type="date"
            className="invoiceInput"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />

          <label className="invoiceLabel">End Date</label>
          <input
            type="date"
            className="invoiceInput"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
        </div>

        {error && <p className="invoiceError">{error}</p>}

        <button
          className="invoiceGenerateBtn"
          onClick={handleGenerate}
          disabled={loading}
        >
          {loading ? 'Generating…' : 'Generate'}
        </button>
      </div>

      {/* Results */}
      {results && (
        <>
          <div className="formSep"></div>
          <div className="invoiceResultsSection">
            <h3 className="invoiceResultsTitle">
              {selectedClient} &mdash; {new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} to {new Date(endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </h3>

            {results.shiftRows.length === 0 ? (
              <p className="invoiceEmpty">No shifts found for this client in the selected date range.</p>
            ) : (
              <div className="invoiceTableWrapper">
                <table className="invoiceTable">
                  <thead>
                    <tr>
                      <th className="invTh">Shift Date</th>
                      <th className="invTh">Caretaker</th>
                      <th className="invTh invRight">Hours</th>
                      <th className="invTh invRight">Rate</th>
                      <th className="invTh invRight">Hours Total</th>
                      <th className="invTh invRight">Flat Fee</th>
                      <th className="invTh invRight">Mileage</th>
                      <th className="invTh invRight">Reimbursements</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.shiftRows.map(row => (
                      <tr key={row.id} className="invRow">
                        <td className="invTd">{formatDate(row.date)}</td>
                        <td className="invTd">{row.caretaker}</td>
                        <td className="invTd invRight">{row.hours != null ? row.hours.toFixed(2) : '—'}</td>
                        <td className="invTd invRight">{row.rate != null ? `$${Number(row.rate).toFixed(2)}` : '—'}</td>
                        <td className="invTd invRight">{row.lineTotal != null ? fmt(row.lineTotal) : '—'}</td>
                        <td className="invTd invRight">{row.flatFee != null ? fmt(row.flatFee) : '—'}</td>
                        <td className="invTd invRight">{row.mileage > 0 ? `${row.mileage} mi` : '—'}</td>
                        <td className="invTd invRight">{row.reimbursement > 0 ? fmt(row.reimbursement) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Subtotals */}
            <div className="invoiceSummary">
              <div className="invSummaryRow">
                <span className="invSummaryLabel">Hours Subtotal</span>
                <span className="invSummaryValue">{fmt(results.totalHoursAmt)}</span>
              </div>
              <div className="invSummaryRow">
                <span className="invSummaryLabel">Flat Fee Shifts</span>
                <span className="invSummaryValue">{fmt(results.totalFlatFees)}</span>
              </div>
              <div className="invSummaryRow">
                <span className="invSummaryLabel">Mileage (@ $0.45/mi)</span>
                <span className="invSummaryValue">{fmt(results.totalMileage)}</span>
              </div>
              <div className="invSummaryRow">
                <span className="invSummaryLabel">Reimbursements</span>
                <span className="invSummaryValue">{fmt(results.totalReimbursements)}</span>
              </div>
              <div className="invSummaryRow invTotalRow">
                <span className="invSummaryLabel">Total</span>
                <span className="invSummaryValue">{fmt(results.grandTotal)}</span>
              </div>
            </div>

            {/* Send to QuickBooks */}
            <div className="invQbRow">
              <button
                className="invQbBtn"
                onClick={handleSendToQB}
                disabled={qbLoading || !qbConnected || results.shiftRows.length === 0}
              >
                {qbLoading ? 'Sending…' : 'Send to QuickBooks'}
              </button>
              {qbStatus && (
                <span className={qbStatus.startsWith('✓') ? 'invQbSuccess' : 'invQbError'}>
                  {qbStatus}
                </span>
              )}
            </div>

            {/* Copy-paste summary */}
            {results.shiftRows.length > 0 && (() => {
              const summaryText = results.shiftRows.map(row =>
                `${formatDateTime(row.date)}; ${formatDateTime(row.end)}; ${row.caretaker}; (${row.hoursWorked.toFixed(2)} hrs)`
              ).join('\n');
              const handleCopy = () => {
                navigator.clipboard.writeText(summaryText).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                });
              };
              return (
                <>
                  <div className="formSep"></div>
                  <div className="invCopySummarySection">
                    <div className="invCopySummaryHeader">
                      <h4 className="invCopySummaryTitle">Shift Summary</h4>
                      <button className="invCopyBtn" onClick={handleCopy}>
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <textarea
                      className="invCopySummaryBox"
                      readOnly
                      value={summaryText}
                      rows={results.shiftRows.length + 1}
                    />
                  </div>
                </>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}
